import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { ApartmentForm } from '../components/ApartmentForm';
import { ApartmentStatusBadge } from '../components/ApartmentStatusBadge';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ICalFeedSection } from '../components/ICalFeedSection';
import { AvailabilitiesLinkCard } from '../components/ApartmentHubSections/AvailabilitiesLinkCard';
import { CoOwnershipSection } from '../components/ApartmentHubSections/CoOwnershipSection';
import { BankTransferConfigSection } from '../components/ApartmentHubSections/BankTransferConfigSection';
import { BasicInfoSection } from '../components/ApartmentHubSections/BasicInfoSection';
import { LocationSection } from '../components/ApartmentHubSections/LocationSection';
import { PropertyDetailsSection } from '../components/ApartmentHubSections/PropertyDetailsSection';
import { FeaturesSection } from '../components/ApartmentHubSections/FeaturesSection';
import { PhotosSection } from '../components/ApartmentHubSections/PhotosSection';
import { ListRefreshButton } from '../components/ListRefreshButton';
import {
  useApartment,
  useCreateApartment,
  usePublishApartment,
  useUnpublishApartment,
  useDeleteApartment,
} from '../hooks/useApartments';
import type { Apartment, RegisterApartmentRequest } from '../types/apartment';
import { is404 } from '../utils/errors';
import { isAuthRequiredError } from '../utils/listRefreshState';

// ── Hub page state discriminated union (per data-model.md) ────────────────
type HubPageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'not-found' }
  | { status: 'ready'; apartment: Apartment }
  | { status: 'new' };

// ── Create-mode sub-component ─────────────────────────────────────────────

const CreateApartmentPage = () => {
  const { t } = useTranslation(['pages', 'common', 'errors']);
  const navigate = useNavigate();
  const createMutation = useCreateApartment();

  const handleSubmit = async (data: RegisterApartmentRequest) => {
    try {
      const created = await createMutation.mutateAsync(data);
      navigate(`/apartments/${created.id}`);
    } catch {
      // ApartmentForm shows no inline error on its own here; parent can add if needed
    }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/apartments')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label={t('pages:backToApartments')}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-3xl font-bold text-gray-900">{t('pages:newApartmentTitle')}</h1>
      </div>
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <ApartmentForm
          onSubmit={handleSubmit}
          onCancel={() => navigate('/apartments')}
          isLoading={createMutation.isPending}
        />
      </div>
    </div>
  );
};

// ── Edit-mode sub-component ───────────────────────────────────────────────

const EditApartmentPage = ({
  apartment,
  isRefreshing,
  onRefresh,
  refreshError,
}: {
  apartment: Apartment;
  isRefreshing: boolean;
  onRefresh: () => void;
  refreshError: string | null;
}) => {
  const { t } = useTranslation(['pages', 'common', 'errors']);
  const navigate = useNavigate();
  const publishMutation = usePublishApartment();
  const unpublishMutation = useUnpublishApartment();
  const deleteMutation = useDeleteApartment();

  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    message: string;
    onConfirm: () => void;
  }>({ open: false, message: '', onConfirm: () => void 0 });

  const [headerError, setHeaderError] = useState<string | null>(null);

  const handlePublish = async () => {
    setHeaderError(null);
    try {
      await publishMutation.mutateAsync(apartment.id);
    } catch {
      setHeaderError(t('errors:somethingWentWrong'));
    }
  };

  const handleUnpublish = async () => {
    setHeaderError(null);
    try {
      await unpublishMutation.mutateAsync(apartment.id);
    } catch {
      setHeaderError(t('errors:somethingWentWrong'));
    }
  };

  const handleDelete = () => {
    setConfirmDialog({
      open: true,
      message: t('pages:deleteApartmentConfirm'),
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, open: false }));
        try {
          await deleteMutation.mutateAsync(apartment.id);
          navigate('/apartments');
        } catch {
          setHeaderError(t('errors:failedToDeleteApartment'));
        }
      },
    });
  };

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} libraries={['places']}>
      <div>
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-white border-b px-0 py-4 mb-8 flex flex-col gap-2">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/apartments')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label={t('pages:backToApartments')}
              >
                <ArrowLeft size={20} />
              </button>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">{apartment.name}</h1>
                <ApartmentStatusBadge status={apartment.status} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <ListRefreshButton
                isRefreshing={isRefreshing}
                onRefresh={onRefresh}
                className="flex items-center gap-2 py-2 px-3 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {(apartment.status === 'DRAFT' || apartment.status === 'UNPUBLISHED') ? (
                <button
                  onClick={handlePublish}
                  disabled={publishMutation.isPending}
                  className="flex items-center gap-2 py-2 px-4 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  {publishMutation.isPending && (
                    <span className="inline-block w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin" />
                  )}
                  {t('pages:publish')}
                </button>
              ) : (
                <button
                  onClick={handleUnpublish}
                  disabled={unpublishMutation.isPending}
                  className="flex items-center gap-2 py-2 px-4 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                >
                  {unpublishMutation.isPending && (
                    <span className="inline-block w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                  )}
                  {t('pages:unpublish')}
                </button>
              )}
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="flex items-center gap-2 py-2 px-4 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-colors font-medium disabled:opacity-50 min-h-[44px]"
              >
                {t('common:delete')}
              </button>
            </div>
          </div>
          {headerError && (
            <p className="text-sm text-red-600 px-2">{headerError}</p>
          )}
          {refreshError && (
            <p className="text-sm text-red-600 px-2">{refreshError}</p>
          )}
        </div>

        {/* Sections */}
        <div className="space-y-6">
          <BasicInfoSection apartment={apartment} />
          <AvailabilitiesLinkCard apartmentId={apartment.id} />
          <LocationSection apartment={apartment} />
          <PhotosSection apartment={apartment} />
          <PropertyDetailsSection apartment={apartment} />
          <FeaturesSection apartment={apartment} />
          <ICalFeedSection apartmentId={apartment.id} />
          <CoOwnershipSection apartmentId={apartment.id} apartment={apartment} />
          <BankTransferConfigSection apartmentId={apartment.id} />
        </div>
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        message={confirmDialog.message}
        intent="danger"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(d => ({ ...d, open: false }))}
      />
    </APIProvider>
  );
};

// ── Main hub page ─────────────────────────────────────────────────────────

const ApartmentHub = () => {
  const { id } = useParams<{ id: string }>();

  // No id param → create mode
  if (!id) {
    return <CreateApartmentPage />;
  }

  return <ApartmentHubEdit id={id} />;
};

// Separate component so hooks can be called unconditionally after id is confirmed
const ApartmentHubEdit = ({ id }: { id: string }) => {
  const { t } = useTranslation(['pages', 'errors', 'common']);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const { data: apartment, isLoading, isFetching, error, refetch } = useApartment(id);

  const handleRefresh = async () => {
    try {
      setRefreshError(null);
      await refetch({ throwOnError: true });
    } catch (err) {
      setRefreshError(isAuthRequiredError(err) ? t('common:signInRequired') : t('common:refreshFailed'));
    }
  };

  let pageState: HubPageState;
  if (isLoading) {
    pageState = { status: 'loading' };
  } else if (error && is404(error)) {
    pageState = { status: 'not-found' };
  } else if (error) {
    pageState = { status: 'error', message: t('errors:somethingWentWrong') };
  } else if (!apartment) {
    pageState = { status: 'not-found' };
  } else {
    pageState = { status: 'ready', apartment };
  }

  if (pageState.status === 'loading') {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('pages:apartmentHubLoading')}</div>
      </div>
    );
  }

  if (pageState.status === 'not-found') {
    return (
      <div className="card bg-yellow-50 border-yellow-200">
        <p className="text-yellow-800 text-center py-12">{t('pages:apartmentNotFoundHub')}</p>
      </div>
    );
  }

  if (pageState.status === 'error') {
    return (
      <div className="card bg-red-50 border-red-200">
        <p className="text-red-600 text-center py-12">{pageState.message}</p>
      </div>
    );
  }

  return (
    <EditApartmentPage
      apartment={pageState.apartment}
      isRefreshing={isFetching}
      onRefresh={handleRefresh}
      refreshError={refreshError}
    />
  );
};

export default ApartmentHub;
